import React, { memo, useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Icon, Modal, Text } from '@Sage/hoi-poi-ui';
import { SectionList } from '@Sage/ssm-web5';

import { CrudActions } from 'actions';
import Button from 'components/Button';
import { DocumentsIcons } from 'components/icons';
import Literal from 'components/Literal';
import { COMPANIES } from 'constants/Entities';
import { CardHeaderWidget } from 'containers/components/detail/card';
import AddEntityDialog from 'containers/components/dialog/AddEntityDialog';
import { getLiteral } from 'utils/getLiteral';
import { warningToast } from 'utils/toast';

import CONFIG from './TableConfig';
import CompanyAddDialogDecorator from './CompanyAddDialogDecorator';

import './styles.scss';

const MAX_COMPANIES = 200;

const deleteModalOverrides = {
    confirmButton: {
        type: 'primary-error',
    },
};

const CompanyAddDialogDecorated = CompanyAddDialogDecorator(AddEntityDialog);

const EmptyView = memo(() => (
    <div className="company-rates-empty">
        <div className="company-rates-empty__content">
            <DocumentsIcons style={{ fontSize: 41, display: 'block', marginBottom: 16 }} />
            <Literal className="hintText" literal="error_no_accounts_added_rate" />
        </div>
    </div>
));

EmptyView.displayName = 'EmptyView';

const CrudCompanyRates = memo(({ field }) => {
    const dispatch = useDispatch();

    // ========================================
    // REDUX STATE & SELECTORS
    // ========================================
    const modelList = useSelector((state) => state.crud.entityCrud.get(field));

    // ========================================
    // DERIVED STATE & MEMOIZATION
    // ========================================
    const dataSize = modelList ? modelList.size : 0;

    // ========================================
    // LOCAL STATE
    // ========================================
    const [open, setOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [rowToDelete, setRowToDelete] = useState(null);

    // ========================================
    // EVENT HANDLERS & CALLBACKS
    // ========================================
    const onChange = useCallback(
        (fieldKey, value) => {
            dispatch(CrudActions.updateFieldCrud(fieldKey, value));
        },
        [dispatch],
    );

    const handleOpen = useCallback(() => {
        if (dataSize >= MAX_COMPANIES) {
            warningToast({
                text: getLiteral('label_price_list_exceeds_accounts'),
            });
            return;
        }
        setOpen(true);
    }, [dataSize]);

    const handleClose = useCallback(() => {
        setOpen(false);
    }, []);

    const openDeleteDialog = useCallback((row) => {
        setRowToDelete(row);
        setDeleteDialogOpen(true);
    }, []);

    const closeDeleteDialog = useCallback(() => {
        setDeleteDialogOpen(false);
        setRowToDelete(null);
    }, []);

    const confirmDeleteLine = useCallback(() => {
        if (!modelList || !onChange || !rowToDelete) return;
        const rowIndex = modelList.findIndex((item) => item.id === rowToDelete.id);
        if (rowIndex !== -1) {
            const newModelList = modelList.delete(rowIndex);
            onChange(field, newModelList);
        }
        closeDeleteDialog();
    }, [modelList, onChange, field, rowToDelete, closeDeleteDialog]);

    const getRowNodeId = useCallback((data) => data.id, []);

    const rowData = useMemo(() => {
        if (!modelList) return [];
        return modelList.toArray().map((model, index) => ({
            id: model.id,
            companyname: model.companyname,
            numLine: index + 1,
        }));
    }, [modelList]);

    const columnDefs = useMemo(() => {
        const config = CONFIG();
        return config.columnDefs;
    }, []);

    const actions = useMemo(
        () => [
            (row) => ({
                icon: <Icon name="close" onClick={() => openDeleteDialog(row)} />,
                tooltip: { content: getLiteral('label_remove_from_list') },
            }),
        ],
        [openDeleteDialog],
    );

    const { domLayout, extraClassName } = useMemo(() => {
        if (!rowData?.length) {
            return {
                domLayout: 'normal',
                extraClassName: 'company-rates-table--empty',
            };
        } else if (rowData.length <= 5) {
            return { domLayout: 'autoHeight', extraClassName: '' };
        }
        return {
            domLayout: 'normal',
            extraClassName: 'company-rates-table--scrollable',
        };
    }, [rowData]);

    const renderAddCompany = () => (
        <Button
            type="secondarySmall"
            text={<Literal literal="action_add" />}
            onClick={handleOpen}
        />
    );

    // ========================================
    // RENDER
    // ========================================
    return (
        <div className="companyRatesField crudTableField">
            <div className="sectionTitle">
                <Literal literal="label_info_accounts_rates" />
            </div>
            <div className="sectionSubtitle">
                <Literal literal="helptext_info_accounts_rates" />
            </div>

            <div className="editTableCard">
                <CardHeaderWidget
                    title={<Literal literal="title_accounts" />}
                    additionalinfo={` (${dataSize})`}
                    rightContent={renderAddCompany()}
                />
                {dataSize > 0 ? (
                    <SectionList
                        id="company-rates-table"
                        className={`company-rates-table ${extraClassName}`}
                        columnDefs={columnDefs}
                        rowData={rowData}
                        getRowNodeId={getRowNodeId}
                        useSort={false}
                        domLayout={domLayout}
                        actions={actions}
                    />
                ) : (
                    <EmptyView />
                )}
                <Modal
                    className="fm-delete-modal"
                    overlayClassName="fm-delete-modal__overlay"
                    overrides={deleteModalOverrides}
                    useCornerClose={false}
                    isOpen={deleteDialogOpen}
                    onCancel={closeDeleteDialog}
                    onConfirm={confirmDeleteLine}
                    title={getLiteral('label_remove_from_list')}
                    confirmText={getLiteral('action_confirm_bn')}
                    cancelText={getLiteral('action_cancel')}
                    size="small"
                >
                    <Text>{getLiteral('confirm_sure')}</Text>
                </Modal>
                <CompanyAddDialogDecorated
                    title={<Literal literal="title_accounts" />}
                    field={field}
                    handleClose={handleClose}
                    open={open}
                    entity={COMPANIES}
                    companiesList={modelList}
                />
            </div>
        </div>
    );
});

CrudCompanyRates.displayName = 'CrudCompanyRates';

export default CrudCompanyRates;
